import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SENSAIPageContext } from "./types";

const SENSAIContext = createContext<SENSAIPageContext | null>(null);

export const useSENSAI = () => {
  const context = useContext(SENSAIContext);
  if (!context) {
    throw new Error("useSENSAI must be used within a SENSAIProvider");
  }
  return context;
};

interface SENSAIProviderProps {
  children: React.ReactNode;
  userRole: "viewer" | "operator" | "supervisor" | "admin";
}

export const SENSAIProvider: React.FC<SENSAIProviderProps> = ({ children, userRole }) => {
  const location = useLocation();
  const [context, setContext] = useState<SENSAIPageContext>({
    pageId: "control-bridge/dashboard",
    toolIds: [],
    userRole,
  });

  useEffect(() => {
    const pathname = location.pathname;
    let pageId = "control-bridge/dashboard";
    let toolIds: string[] = [];

    if (pathname.includes("/control-bridge")) {
      if (pathname.includes("/projects")) {
        pageId = "control-bridge/projects";
        toolIds = ["ProjectDashboard", "KanbanBoard", "ProjectFilters"];
      } else if (pathname.includes("/support-bridge")) {
        pageId = "control-bridge/support-bridge";
        toolIds = ["TicketList", "SLABadge", "TicketForm"];
      } else if (pathname.includes("/echogrid")) {
        pageId = "control-bridge/echogrid/appraisal";
        toolIds = ["MetricCards", "JurisdictionMap", "ETLRefresh"];
      } else if (pathname.includes("/soma")) {
        pageId = "control-bridge/soma";
        toolIds = ["HandoffTimeline", "SwarmDecay", "AutoFix"];
      } else if (pathname.includes("/hivegrid")) {
        pageId = "control-bridge/hivegrid/etl-wizard";
        toolIds = ["ETLSource", "ETLOutputs", "ETLDeploy"];
      } else if (pathname.includes("/settings")) {
        pageId = "control-bridge/settings/rbac";
        toolIds = ["RBACRoles", "UserAssignment"];
      } else if (pathname.includes("/widgets")) {
        pageId = "control-bridge/widgets";
        toolIds = ["DashboardWidgets", "MetricCards", "ResourceAllocation"];
      } else if (pathname.includes("/presentation-studio")) {
        pageId = "control-bridge/presentation-studio";
        toolIds = ["PresentationStudio", "BlueAI", "ColorPalettes", "AssetManager"];
      }
    }

    setContext({
      pageId,
      toolIds,
      userRole,
      filters: {},
    });
  }, [location.pathname, userRole]);

  return (
    <SENSAIContext.Provider value={context}>
      {children}
    </SENSAIContext.Provider>
  );
};
